// import

import cx from 'clsx';
import {styled} from 'goober';
import {useForm} from 'react-hook-form';

import type {CtaNode} from 'libs/model';
import {SignupForm} from 'modules/SignupForm';
import {ZigZag} from 'modules/Svg';
import {useTheme} from 'modules/Theme';
import {Type} from 'modules/Type';

import {Book} from './Book';

// types

type Props = {
  cta: CtaNode;
};

// vars

export const buttonColors = {
  red: 'yellow',
  orange: 'blue',
  yellow: 'blue',
  green: 'yellow',
  blue: 'orange',
  purple: 'yellow',
  gray: 'gray',
};

// component

export function SignupHeader(props: Props) {
  const {cta: {body, meta}, ...rest} = props;
  const {media: {coverImage, vectorImage, alt}, headline, themeColor = 'blue', buttonText, mailerliteGroups} = meta;
  const form = useForm();
  const {color, palette} = useTheme();
  const colorPick = color[themeColor];
  const hasMedia = coverImage || vectorImage;

  return (
    <Header {...rest} data-test="blog-header" style={{'--bg': colorPick}}>
      <div className="content">
        <div className="inner">
          {hasMedia ? (
            <div className="media">
              {coverImage ? (
                <Book
                  coverColor={palette[themeColor][50]}
                  coverImage={coverImage}
                  title={alt ?? headline}
                />
              ) : vectorImage ? (
                <img
                  alt={alt ?? headline}
                  className="image"
                  src={vectorImage}
                />
              ) : null}
            </div>
          ) : null}

          <div className="copy">
            <div
              className={cx('title', {
                medium: headline.length < 50,
                short: headline.length < 35,
              })}
            >
              {headline}
            </div>

            <Type className="blurb">
              {body.trim() ? (
                <div dangerouslySetInnerHTML={{__html: body}} />
              ) : null}

              <SignupForm
                buttonClass={cx('button', buttonColors[themeColor])}
                buttonText={buttonText}
                className="form"
                form={form}
                groups={mailerliteGroups}
              />
            </Type>
          </div>
        </div>
      </div>

      <ZigZag flip color={themeColor} id="signup-header" />
    </Header>
  );
}

// styles

const Header = styled('aside')`
  .content {
    padding: 6rem 3rem;
    position: relative;
    background-color: var(--bg);
    width: 100%;
  }

  .inner {
    margin: 0 auto;
    max-width: var(--bp-m);
    display: flex;
    gap: 1.5rem;
  }

  .media {
    flex: 0 1 clamp(9rem, 33%, 18rem);
    max-height: 24rem;
  }

  .image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: top;
  }

  .copy {
    flex: 0 1 66%;
    align-self: center;
  }

  .title {
    font-family: var(--font-head);
    font-size: 2.25rem;
    line-height: 2.75rem;
    letter-spacing: 0;
    font-weight: 500;
    margin: 0 0 0.5rem;

    &.medium {
      font-size: 3rem;
      line-height: 3.5rem;
    }

    &.short {
      font-size: 4rem;
      line-height: 4.25rem;
    }
  }

  .blurb {
    line-height: 1.75rem;

    div > * { margin-bottom: 1rem; }
  }

  .form {
    display: grid;
    gap: 0.5rem;
    align-items: start;
    grid-template-columns: minmax(auto, 65%) auto;

    input[type="email"] { margin-bottom: 0; }

    button {
      height: 2.5rem;
      line-height: 2.5rem;
      padding: 0 1rem;
      white-space: nowrap;
    }
  }

  @media (max-width: 53.99875em) {
    .title.short {
      font-size: 3rem;
      line-height: 3.5rem;
    }
  }

  @media (max-width: 44.99875em) {
    .content {
      padding: 3rem 1.5rem;
    }

    .inner {
      max-width: var(--bp-s);
      flex-flow: column;
    }

    .media {
      align-self: center;
      width: clamp(9rem, 50%, 18rem);
    }

    .image {
      max-height: 21rem;
    }
  }

  @media (max-width: 26.99875em) {
    .title.short,
    .title.medium {
      font-size: 2.25rem;
      line-height: 2.75rem;
    }

    .form {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
    }
  }
`;
